import { getLocale } from '@raisin/cp-utils';
import { productTypes, promotionTypes } from 'types';
import {
  Config,
  CountryConfig,
  OffersTableConfig,
  config,
  sortingTypes,
  sortingDirections,
} from './config';
import { safelyParseJSON } from './utils';

export interface OffersTableProps {
  isLoggedIn?: boolean;
  locale?: string;
  tabs?: {
    default_tab?: string;
    product_category_tabs?: string[];
    promotional_tabs?: string[];
  };
  filters?: {
    finanztest_bestenliste?: boolean;
    country_rating: string[];
    terms_to_include: number[];
    default_investment_term?: string;
    bank_filter?: {
      is_exclude: boolean;
      selected_banks: {
        bank_uid: string;
        entry_uid: string;
      }[];
    };
    country_filter?: {
      countries?: string[];
      is_exclude?: boolean;
    };
  };
  user_filters_visibility?: {
    show_flexgeld_filter: boolean;
    show_term_filter: boolean;
    show_country_rating_filter: boolean;
    user_can_filter_by_dtb_country: boolean;
  };
  display_config?: {
    show_decimal_places_for_interest_and_bonus?: boolean;
    sorting?: string;
  };
  cta?: {
    show_cta?: boolean;
    cta_label?: string;
    cta_link?: string;
    cta_link_mobile?: string;
  };
  isWrappedByLayoutContainer?: boolean;
}

const tabsMap = {
  TERM: productTypes.TERM_DEPOSIT,
  OVERNIGHT: productTypes.OVERNIGHT_DEPOSIT,
  NOTICE: productTypes.NOTICE_ACCOUNT,
  INVEST: promotionTypes.RAISIN_INVEST,
  PENSION: promotionTypes.RAISIN_PENSION,
};

export const getCountrySpecificConfig = (defaultConfig: Config, locale?: string): CountryConfig => {
  const DEFAULT_LOCALE = 'pl-PL';
  const env = getLocale(locale || DEFAULT_LOCALE)?.iso3.toUpperCase();

  return defaultConfig[`${env}Config`] || {};
};

const getSortingConfig = (sorting: string = '') => {
  const [byKey, direction] = sorting?.split('.') ?? [];

  if (byKey && direction) return { byKey, direction };

  return { byKey: sortingTypes.rate, direction: sortingDirections.desc };
};

const getFilterConfig = (
  defaultConfig: OffersTableConfig['filter'],
  countrySpecificFilterConfig: CountryConfig['filter'],
  filters: OffersTableProps['filters'],
) => {
  const defaultInvestmentTerm =
    filters?.default_investment_term && safelyParseJSON(filters?.default_investment_term);
  return {
    ...defaultConfig,
    currency: countrySpecificFilterConfig?.currency || defaultConfig.currency,
    type: defaultConfig.type,
    investmentAmount:
      countrySpecificFilterConfig?.investmentAmount || defaultConfig.investmentAmount,
    term: defaultInvestmentTerm || defaultConfig.term,
    showForeignCurrency: defaultConfig.showForeignCurrency || false,
    bankFilter: {
      isExclude: filters?.bank_filter?.is_exclude ?? true,
      banks: filters?.bank_filter?.selected_banks?.map?.((bank) => bank.bank_uid) ?? [],
    },
    countriesFilter: {
      countriesToShow: filters?.country_filter?.countries ?? [],
      isExclude: filters?.country_filter?.is_exclude,
    },
    termsToInclude: filters?.terms_to_include ?? [],
    showOnlyFinanztestBestenlisteProducts:
      filters?.finanztest_bestenliste || defaultConfig.showOnlyFinanztestBestenlisteProducts,
    ratingsToInclude: filters?.country_rating ?? [],
  };
};

const getDefaultTab = (defaultTab, productCategoryTabs) => {
  if (!defaultTab) {
    return tabsMap[productCategoryTabs[0]];
  }

  if (!productCategoryTabs.includes(defaultTab)) {
    return tabsMap[productCategoryTabs[0]];
  }

  return tabsMap[defaultTab];
};

const getActivatedTabs = (productCategoryTabs) =>
  productCategoryTabs.length > 0
    ? productCategoryTabs.map((cat) => tabsMap[cat])
    : [
        productTypes.FLEXGELD,
        productTypes.FORREIGN_CURRENCY,
        productTypes.NOTICE_ACCOUNT,
        productTypes.OVERNIGHT_DEPOSIT,
        productTypes.TERM_DEPOSIT,
      ];

export const getConfig = ({
  locale,
  filters,
  user_filters_visibility,
  tabs = {},
  display_config = {},
  cta = {},
}: OffersTableProps) => {
  const {
    default_tab: defaultTab = '',
    product_category_tabs: productCategoryTabs = ['TERM', 'OVERNIGHT', 'NOTICE'],
    promotional_tabs: promotionalTabs = [],
  } = tabs;
  const countrySpecificConfig = getCountrySpecificConfig(config, locale);
  const enableTabs =
    productCategoryTabs.length > 1 || (productCategoryTabs.length && promotionalTabs.length);
  const activatedTabs = getActivatedTabs([...productCategoryTabs, ...promotionalTabs]);

  return {
    ...config,
    offersTableConfig: {
      environment: config.environment,
      environmentCurrency: countrySpecificConfig?.environmentCurrency || config.environmentCurrency,
      investNowButtonText: cta?.cta_label || null,
      defaultRate: countrySpecificConfig?.defaultRate || config?.offersTableConfig?.defaultRate,
      showEasyAccessLabel: countrySpecificConfig?.showEasyAccessLabel || null,
      filter: getFilterConfig(
        config.offersTableConfig.filter,
        countrySpecificConfig.filter,
        filters,
      ),
      order: getSortingConfig(display_config?.sorting),
      features: {
        ...config.offersTableConfig.features,
        showFlexgeldFilter: user_filters_visibility?.show_flexgeld_filter || false,
        showInvestmentAmountFilter: true,
        showForeignCurrencyFilter: false,
        showCountryCreditRatingFilter: user_filters_visibility?.show_country_rating_filter ?? false,
        showCountryFilter: user_filters_visibility?.user_can_filter_by_dtb_country ?? false,
        showDecimalPlacesForInterestAndBonus:
          display_config?.show_decimal_places_for_interest_and_bonus ?? false,
        showBonus: true,
        showTermFilter: !!user_filters_visibility?.show_term_filter,
        showCTAButtons: cta?.show_cta,
        isTermFetchedFromBE: true,
        ...countrySpecificConfig.features,
      },
    },
    BUTTON_LINK_CTA_DESKTOP:
      (cta?.cta_link || countrySpecificConfig.BUTTON_LINK_CTA_DESKTOP) ??
      config.BUTTON_LINK_CTA_DESKTOP,
    BUTTON_LINK_CTA_MOBILE:
      (cta?.cta_link_mobile || cta?.cta_link || countrySpecificConfig.BUTTON_LINK_CTA_MOBILE) ??
      config.BUTTON_LINK_CTA_MOBILE,
    ENABLE_TABS: enableTabs,
    CUSTOMER_GROUP: config.CUSTOMER_GROUP,
    ACTIVATED_TABS: activatedTabs,
    DEFAULT_TAB: getDefaultTab(defaultTab, productCategoryTabs),
  };
};
